'use client';

import {
  FileTree,
  FileTreeFile,
  FileTreeFolder,
} from '@/components/ai-elements/file-tree';
import {
  SandboxTabContent,
  SandboxTabs,
  SandboxTabsBar,
  SandboxTabsList,
  SandboxTabsTrigger,
} from '@/components/ai-elements/sandbox';
import { Snippet, SnippetCopyButton, SnippetInput } from '@/components/ai-elements/snippet';
import { Terminal } from '@/components/ai-elements/terminal';
import { cn } from '@/lib/utils';
import { CheckCircle, FileCode, FolderOpen, WarningCircle } from '@phosphor-icons/react';

interface DevToolArtifactRendererProps {
  toolName: string;
  input?: Record<string, unknown>;
  output?: unknown;
  className?: string;
  displayContext?: 'inline' | 'artifact';
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function asString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function outputRecord(output: unknown) {
  const record = asRecord(output);
  return asRecord(record?.data) ?? record ?? {};
}

function basename(path: string) {
  const normalized = path.replace(/\/+$/, '');
  return normalized.split('/').filter(Boolean).at(-1) ?? normalized;
}

function commandOutput(output: unknown) {
  const data = outputRecord(output);
  return [
    asString(data.result),
    asString(data.output),
    asString(data.stdout),
    asString(data.stderr),
  ].filter(Boolean).join('\n');
}

function commandTitle(toolName: string, input?: Record<string, unknown>, output?: unknown) {
  const data = outputRecord(output);
  const command = asString(data.command) ?? asString(input?.command);
  if (command) return command;
  if (toolName === 'sandboxCodeRun') return 'Code run';
  if (toolName === 'sandboxSessionLogs') return 'Terminal logs';
  if (toolName === 'sandboxSessionExec') return 'Terminal command';
  return 'Command';
}

function statusLine(output: unknown) {
  const data = outputRecord(output);
  const exitCode = typeof data.exitCode === 'number' ? data.exitCode : undefined;
  if (exitCode === undefined) return undefined;
  return exitCode === 0 ? 'Exited successfully' : `Exited with code ${exitCode}`;
}

function FilesView({ input, output }: { input?: Record<string, unknown>; output?: unknown }) {
  const data = outputRecord(output);
  const files = Array.isArray(data.files) ? data.files : [];
  const rootPath = asString(data.path) ?? asString(input?.path) ?? '/';

  return (
    <FileTree
      defaultExpanded={new Set([rootPath])}
      className="h-full overflow-auto rounded-none border-0 bg-transparent"
    >
      <FileTreeFolder path={rootPath} name={basename(rootPath) || rootPath}>
        {files.length === 0 ? (
          <div className="px-2 py-3 text-sm text-muted-foreground">No files returned.</div>
        ) : files.map((item, index) => {
          const file = asRecord(item) ?? {};
          const name = asString(file.name) ?? `item-${index + 1}`;
          const path = `${rootPath.replace(/\/+$/, '')}/${name}`;
          const isDir = file.isDir === true;
          return isDir
            ? <FileTreeFolder key={path} path={path} name={name} />
            : <FileTreeFile key={path} path={path} name={name} />;
        })}
      </FileTreeFolder>
    </FileTree>
  );
}

function FileContentView({ input, output }: { input?: Record<string, unknown>; output?: unknown }) {
  const data = outputRecord(output);
  const path = asString(data.path) ?? asString(input?.path) ?? asString(input?.pathOrId) ?? 'file';
  const content = asString(data.content) ?? '';
  const truncated = data.truncated === true;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border bg-background">
      <div className="flex min-h-10 items-center justify-between gap-3 border-b bg-muted/30 px-3">
        <div className="flex min-w-0 items-center gap-2">
          <FileCode weight="duotone" className="size-4 text-muted-foreground" />
          <p className="truncate text-sm font-medium">{path}</p>
        </div>
        {truncated && (
          <span className="shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-600 dark:text-amber-300">
            truncated
          </span>
        )}
      </div>
      <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-sm leading-relaxed">
        {content || 'No content returned.'}
      </pre>
    </div>
  );
}

function SourceFieldsView({ output }: { output?: unknown }) {
  const data = outputRecord(output);
  const fields = asRecord(data.fields) ?? {};
  const entries = Object.entries(fields).filter(([, value]) => typeof value === 'string') as Array<[string, string]>;

  if (entries.length === 0) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center rounded-lg border bg-muted/20 p-6 text-center">
        <div>
          <FileCode weight="duotone" className="mx-auto size-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">No source fields returned</p>
          <p className="mt-1 text-sm text-muted-foreground">Ask for specific source fields such as html, css, or componentCode.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-auto space-y-3">
      {entries.map(([field, value]) => (
        <div key={field} className="overflow-hidden rounded-lg border bg-background">
          <div className="flex min-h-9 items-center justify-between gap-3 border-b bg-muted/30 px-3">
            <p className="truncate text-sm font-medium">{field}</p>
            <span className="text-xs text-muted-foreground">{value.length.toLocaleString()} chars</span>
          </div>
          <pre className="max-h-[460px] overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-sm leading-relaxed">
            {value}
          </pre>
        </div>
      ))}
    </div>
  );
}

function GrepResultsView({ output }: { output?: unknown }) {
  const data = outputRecord(output);
  const matches = Array.isArray(data.matches) ? data.matches : [];
  return (
    <div className="h-full min-h-0 overflow-auto space-y-3">
      <div className="rounded-lg border bg-background p-4">
        <p className="text-sm font-medium">
          {typeof data.totalMatches === 'number' ? data.totalMatches : matches.length} matches
        </p>
        {asString(data.pattern) && (
          <p className="mt-1 text-sm text-muted-foreground">Pattern: {asString(data.pattern)}</p>
        )}
      </div>
      {matches.map((item, index) => {
        const match = asRecord(item) ?? {};
        const field = asString(match.field) ?? 'field';
        const lineNumber = typeof match.lineNumber === 'number' ? match.lineNumber : undefined;
        const line = asString(match.line) ?? '';
        return (
          <div key={`${field}-${lineNumber ?? index}`} className="overflow-hidden rounded-lg border bg-background">
            <div className="border-b bg-muted/30 px-3 py-2 text-sm font-medium">
              {field}{lineNumber ? `:${lineNumber}` : ''}
            </div>
            <pre className="overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-sm">{line}</pre>
          </div>
        );
      })}
    </div>
  );
}

function ResultSummary({ toolName, input, output }: DevToolArtifactRendererProps) {
  const data = outputRecord(output);
  const success = asRecord(output)?.success !== false;
  const path = asString(data.path) ?? asString(input?.path) ?? asString(input?.destinationPath);
  const bytes = typeof data.bytes === 'number' ? data.bytes : undefined;
  const sessionName = asString(data.sessionName) ?? asString(input?.sessionName);
  const count = typeof data.count === 'number' ? data.count : undefined;

  return (
    <div className="rounded-lg border bg-background p-4">
      <div className="flex items-start gap-3">
        <span className={cn(
          'mt-0.5 grid size-8 shrink-0 place-items-center rounded-md border',
          success ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-500' : 'border-destructive/25 bg-destructive/10 text-destructive',
        )}>
          {success ? <CheckCircle weight="duotone" className="size-4" /> : <WarningCircle weight="duotone" className="size-4" />}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium">{success ? 'Tool completed' : 'Tool failed'}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {[toolName, path, sessionName, count !== undefined ? `${count} items` : undefined, bytes !== undefined ? `${bytes} bytes` : undefined]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </div>
    </div>
  );
}

function TerminalView({ toolName, input, output, fill }: DevToolArtifactRendererProps & { fill?: boolean }) {
  const title = commandTitle(toolName, input, output);
  const outputText = commandOutput(output);
  const state = statusLine(output);
  return (
    <div className={cn('space-y-3', fill && 'flex h-full min-h-0 flex-col')}>
      {asString(input?.command) && (
        <Snippet code={asString(input?.command)!}>
          <SnippetInput />
          <SnippetCopyButton />
        </Snippet>
      )}
      <Terminal
        output={outputText || state || 'No terminal output returned.'}
        className={cn(fill && 'min-h-0 flex-1')}
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-zinc-200">{title}</p>
            {state && <p className="text-xs text-zinc-500">{state}</p>}
          </div>
        </div>
        <div className={cn(fill ? 'min-h-0 flex-1' : 'max-h-96', 'overflow-auto p-4 font-mono text-sm leading-relaxed')}>
          <pre className="whitespace-pre-wrap break-words">{outputText || 'No terminal output returned.'}</pre>
        </div>
      </Terminal>
    </div>
  );
}

export function DevToolArtifactRenderer(props: DevToolArtifactRendererProps) {
  const { toolName, input, output, className, displayContext = 'inline' } = props;
  const isArtifact = displayContext === 'artifact';
  const hasTerminal = [
    'sandboxExec',
    'sandboxCodeRun',
    'sandboxSessionExec',
    'sandboxSessionLogs',
  ].includes(toolName);
  const hasFiles = ['sandboxListFiles', 'browserListFiles'].includes(toolName);
  const hasFileContent = [
    'sandboxReadFile',
    'browserReadFile',
    'readGalexiaFile',
    'readGalexiaFileLines',
    'readVisualizationLines',
  ].includes(toolName);
  const hasSourceFields = toolName === 'getVisualizationSource';
  const hasGrepResults = toolName === 'grepVisualization';

  if (hasTerminal && !isArtifact) {
    return <TerminalView {...props} className={className} />;
  }
  if (hasFiles && !isArtifact) {
    return <div className={className}><FilesView input={input} output={output} /></div>;
  }
  if (hasFileContent && !isArtifact) {
    return <div className={className}><FileContentView input={input} output={output} /></div>;
  }
  if (hasSourceFields && !isArtifact) {
    return <div className={className}><SourceFieldsView output={output} /></div>;
  }
  if (hasGrepResults && !isArtifact) {
    return <div className={className}><GrepResultsView output={output} /></div>;
  }

  const tabs = [
    hasTerminal ? { value: 'terminal', label: 'Terminal' } : undefined,
    hasFiles ? { value: 'files', label: 'Files' } : undefined,
    hasFileContent ? { value: 'file', label: 'File' } : undefined,
    hasSourceFields ? { value: 'source', label: 'Source' } : undefined,
    hasGrepResults ? { value: 'matches', label: 'Matches' } : undefined,
    { value: 'details', label: 'Details' },
  ].filter(Boolean) as Array<{ value: string; label: string }>;
  const defaultTab = tabs[0]?.value ?? 'details';

  return (
    <div className={cn('h-full min-h-0 w-full', className)}>
      <SandboxTabs defaultValue={defaultTab} className="h-full min-h-0">
        <SandboxTabsBar>
          <SandboxTabsList>
            {tabs.map((tab) => (
              <SandboxTabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </SandboxTabsTrigger>
            ))}
          </SandboxTabsList>
        </SandboxTabsBar>
        {hasTerminal && (
          <SandboxTabContent value="terminal" className="h-[calc(100%-41px)] min-h-0 p-3">
            <TerminalView {...props} fill />
          </SandboxTabContent>
        )}
        {hasFiles && (
          <SandboxTabContent value="files" className="h-[calc(100%-41px)] min-h-0 overflow-hidden p-0">
            <FilesView input={input} output={output} />
          </SandboxTabContent>
        )}
        {hasFileContent && (
          <SandboxTabContent value="file" className="h-[calc(100%-41px)] min-h-0 p-3">
            <FileContentView input={input} output={output} />
          </SandboxTabContent>
        )}
        {hasSourceFields && (
          <SandboxTabContent value="source" className="h-[calc(100%-41px)] min-h-0 p-3">
            <SourceFieldsView output={output} />
          </SandboxTabContent>
        )}
        {hasGrepResults && (
          <SandboxTabContent value="matches" className="h-[calc(100%-41px)] min-h-0 p-3">
            <GrepResultsView output={output} />
          </SandboxTabContent>
        )}
        <SandboxTabContent value="details" className="h-[calc(100%-41px)] min-h-0 overflow-auto p-3">
          <ResultSummary toolName={toolName} input={input} output={output} />
        </SandboxTabContent>
      </SandboxTabs>
    </div>
  );
}
